import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Scale, Building2, UserX, ShieldCheck, Boxes, BadgeCheck, ArrowRight,
  ClipboardCheck, CheckCircle2, AlertTriangle, RotateCcw,
} from "lucide-react";

export const HERO = {
  badge: "Adeguamento Normativo 2024/2025",
  title: "Polizza Catastrofale Obbligatoria — Adeguamento Normativo 2024/2025",
  subtitle:
    "La soluzione Assimoco per adempiere all'obbligo di legge e proteggere i beni dell'impresa da eventi naturali estremi.",
};

const OBBLIGO = [
  "Dal 2024 le imprese iscritte al Registro delle Imprese devono dotarsi di una copertura assicurativa contro le calamità naturali.",
  "L'obbligo riguarda la protezione dei beni materiali utilizzati per l'attività d'impresa, con garanzie minime stabilite dalla normativa nazionale.",
];
const OBBLIGATI = [
  "Imprese individuali", "Società di persone", "Società di capitali",
  "Cooperative", "Start-up e PMI innovative", "ETS che svolgono attività d'impresa",
];
const ESCLUSI = [
  "Professionisti iscritti ad albi professionali (non imprese)",
  "Associazioni senza attività d'impresa", "Enti pubblici",
  "Imprese agricole con coperture già previste da specifiche normative settoriali",
  "Soggetti che non possiedono beni materiali rilevanti ai fini dell'attività",
];
const GARANZIE = [
  "Alluvione", "Inondazione", "Allagamento", "Terremoto", "Sisma",
  "Frane e smottamenti", "Eventi atmosferici estremi (definizione IVASS)",
];
const BENI = [
  "Fabbricati", "Terreni", "Impianti", "Macchinari",
  "Attrezzature industriali e commerciali", "Beni strumentali utilizzati per l'attività",
];
const SOLUZIONE = [
  "Garanzie obbligatorie incluse", "Possibilità di estensioni facoltative",
  "Valutazione del rischio automatizzata", "Emissione digitale con firma OTP",
  "Gestione integrata tramite Portale Partner", "Supporto dedicato per cooperative, ETS e PMI",
];

const CHECK_QUESTIONS = [
  { key: "impresa", label: "Svolgi attività d'impresa (non solo professione o associazione senza impresa)?" },
  { key: "registro", label: "Sei iscritto al Registro delle Imprese?" },
  { key: "beni", label: "Possiedi o hai in uso (locazione onerosa o a titolo gratuito) beni materiali strumentali (fabbricati, impianti, macchinari, attrezzature)?" },
];

export function ObbligoCheck({ renderCta }) {
  const [ans, setAns] = useState({});
  const set = (k, v) => setAns((s) => ({ ...s, [k]: v }));
  const done = CHECK_QUESTIONS.every((q) => ans[q.key] !== undefined);
  const obbligato = done && ans.impresa && ans.registro && ans.beni;
  const reset = () => setAns({});

  return (
    <div data-testid="catnat-check" className="rounded-2xl border border-forest/20 bg-white p-6 md:p-8">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-forest text-white">
          <ClipboardCheck className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div>
          <h2 className="font-heading text-lg font-semibold text-forest md:text-xl">Verifica rapida dell'obbligo</h2>
          <p className="text-xs text-muted-foreground">3 domande, nessun dato raccolto — solo orientativo.</p>
        </div>
      </div>

      <div className="space-y-3">
        {CHECK_QUESTIONS.map((q, i) => (
          <div key={q.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-forest/90"><span className="font-medium text-forest">{i + 1}.</span> {q.label}</p>
            <div className="flex shrink-0 gap-2">
              {[["Sì", true], ["No", false]].map(([label, val]) => (
                <button key={label} type="button" data-testid={`check-${q.key}-${val ? "si" : "no"}`}
                  onClick={() => set(q.key, val)}
                  className={`rounded-full border px-4 py-1 text-sm font-medium transition-colors ${
                    ans[q.key] === val ? "border-forest bg-forest text-white" : "border-border text-forest/70 hover:bg-sage"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {done && (
        <div data-testid="check-result"
          className={`mt-5 rounded-xl border p-4 ${obbligato ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
          <div className="flex items-start gap-2.5">
            {obbligato
              ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />}
            <div>
              <p className={`text-sm font-semibold ${obbligato ? "text-emerald-800" : "text-amber-800"}`}>
                {obbligato
                  ? "In base alle risposte, l'impresa rientra nell'obbligo di copertura CAT NAT."
                  : "In base alle risposte, l'impresa potrebbe non rientrare (o rientrare parzialmente) nell'obbligo."}
              </p>
              <p className={`mt-1 text-xs ${obbligato ? "text-emerald-700" : "text-amber-700"}`}>
                {obbligato
                  ? "Procedi con la richiesta di proposta personalizzata."
                  : "Consigliata comunque una valutazione del rischio per confermare la posizione. L'esito è puramente orientativo."}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {renderCta(obbligato)}
            <button type="button" onClick={reset} data-testid="check-reset"
              className="flex items-center gap-1.5 text-xs font-medium text-forest/70 hover:text-forest">
              <RotateCcw className="h-3.5 w-3.5" /> Rifai il check
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, n, title, children }) => (
  <section data-testid={`catnat-section-${n}`} className="rounded-2xl border border-border/60 bg-white p-6 md:p-8">
    <div className="mb-4 flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sage text-forest">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <h2 className="font-heading text-lg font-semibold text-forest md:text-xl">{title}</h2>
    </div>
    {children}
  </section>
);

const Chips = ({ items, tone = "sage" }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((t) => (
      <span key={t}
        className={`rounded-full px-3 py-1.5 text-sm font-medium ${
          tone === "sage" ? "bg-sage/60 text-forest" : "bg-slate-100 text-slate-600"}`}>
        {t}
      </span>
    ))}
  </div>
);

const Bullets = ({ items, accent = "text-forest" }) => (
  <ul className="grid gap-2.5 sm:grid-cols-2">
    {items.map((t) => (
      <li key={t} className="flex items-start gap-2.5 text-sm text-forest/90">
        <ArrowRight className={`mt-0.5 h-4 w-4 shrink-0 ${accent}`} strokeWidth={2} />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export function CatNatInfoSections() {
  return (
    <>
      <Section icon={Scale} n="1" title="L'obbligo di legge in breve">
        <div className="space-y-2.5 text-sm leading-relaxed text-forest/90">
          {OBBLIGO.map((p) => <p key={p}>{p}</p>)}
        </div>
      </Section>

      <div className="grid gap-6 md:grid-cols-2">
        <Section icon={Building2} n="2" title="Chi è obbligato">
          <Bullets items={OBBLIGATI} accent="text-emerald-600" />
        </Section>
        <Section icon={UserX} n="3" title="Chi è escluso">
          <Bullets items={ESCLUSI} accent="text-terracotta" />
        </Section>
      </div>

      <Section icon={ShieldCheck} n="4" title="Garanzie obbligatoriamente comprese">
        <p className="mb-3 text-sm text-muted-foreground">La normativa richiede che la polizza includa almeno:</p>
        <Chips items={GARANZIE} />
      </Section>

      <Section icon={Boxes} n="5" title="Beni assicurabili">
        <p className="mb-3 text-sm text-muted-foreground">La copertura deve riguardare i beni materiali dell'impresa:</p>
        <Chips items={BENI} tone="slate" />
      </Section>

      <Section icon={BadgeCheck} n="6" title="La soluzione Assimoco">
        <p className="mb-3 text-sm text-muted-foreground">
          Assimoco mette a disposizione una polizza CAT NAT conforme alla normativa, con:
        </p>
        <Bullets items={SOLUZIONE} accent="text-forest" />
      </Section>
    </>
  );
}
